import { inject, Injectable, Type } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { numberValidator } from '@shared/validators/number-validator';
import { camelCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { IExpectedKpiForm } from '../../../expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi/expected-kpi.interface';
import { ExpectedKpiService } from '../../../expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi/expected-kpi.service';
import { ExecutionComponent } from './execution.component';
import { EExecutionType, IExecution, IExecutionForm } from './execution.interface';

@Injectable({
  providedIn: 'root',
})
export class ExecutionService {
  private readonly translate = inject(TranslateService);

  public static getExecutionCountTargetValidators(executionType: EExecutionType): ValidatorFn[] {
    return [
      Validators.min(1),
      numberValidator(),
      ...(executionType === EExecutionType.undesignated ? [Validators.required] : []),
    ];
  }

  public getConfig(): {
    component: Type<ExecutionComponent>;
    createForm: (
      platformFeature: EPlatformFeature,
      item?: IExecution,
      creatorId?: string | null,
      isViewMode?: boolean,
    ) => FormGroup<IExecutionForm>;
  } {
    return {
      component: ExecutionComponent,
      createForm: (
        platformFeature: EPlatformFeature,
        item?: IExecution,
        creatorId?: string | null,
        isViewMode?: boolean,
      ): FormGroup<IExecutionForm> => {
        const form = new FormGroup<IExecutionForm>({
          customCtaValues: new FormControl<ICustomCtaValue[]>(item?.customCtaValues ?? [], { nonNullable: true }),
          duration: new FormControl<number | null>(item?.duration ?? null, [Validators.min(0.1), numberValidator()]),
          exclusiveTo: new FormControl<string | null>(item?.exclusiveTo || creatorId || null),
          executionCountTarget: new FormControl<number | null>(
            item?.executionCountTarget ?? 1,
            ExecutionService.getExecutionCountTargetValidators(item?.executionType ?? EExecutionType.undesignated),
          ),
          executionDate: new FormControl<Date | null>(
            item?.executionDate ?? null,
            item?.executionType === EExecutionType.unique ? [Validators.required] : [],
          ),
          executionTime: new FormControl<Date | null>(item?.executionTime ?? null),
          executionType: new FormControl<EExecutionType | null>(item?.executionType ?? null, [Validators.required]),
          expectedKpis: new FormArray<FormGroup<IExpectedKpiForm>>(
            item?.expectedKpis?.map((kpi) => ExpectedKpiService.getConfig().createForm(kpi)) ?? [],
          ),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(item?.globalCtaValues ?? null),
          id: new FormControl<string | null>(item?.id ?? uuidv4()),
          isNew: new FormControl<boolean>(!item?.id),
          name: new FormControl<string | null>(
            item?.name ?? `${this.translate.instant(`shared.platformFeatures.${camelCase(platformFeature)}`)}`,
            [Validators.required, Validators.maxLength(255)],
          ),
        });

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
